/**
 * Build an RFC 5545 iCalendar payload for a Trip. Pure function: no I/O,
 * no timezone lookup, no external deps. Callers (the export endpoint, the
 * mobile UI) decide how to deliver the bytes.
 *
 * Time handling:
 *   - Place / CustomEntry have HH:mm anchored to a Day's calendar date.
 *     We emit DTSTART/DTEND as floating local times (no Z, no TZID) — the
 *     calendar app interprets them in the viewer's local zone, which is
 *     the right thing while you're in the destination. Adding TZID waits
 *     on roadmap #13 (timezone-aware times) once Day.timezone exists.
 *   - Journey origin/destination are full ISO datetimes (already day-
 *     spanning). Same floating-time treatment for now; an overnight
 *     flight will correctly cross midnight.
 *   - Stay checkIn/checkOut are full ISO datetimes — single VEVENT span.
 *
 * Skipped for v1: Day-as-allday events (redundant with the per-place
 * events) and auto-generated transports (visual noise; the Place→Place
 * gap is implicit).
 */
import type { Trip, Place, CustomEntry, Journey, Stay, Day } from './types';

const PRODID = '-//Kiips//Trip Calendar 1.0//EN';

export interface BuildIcsOptions {
  /** Used in PRODID + VEVENT UID domain. Defaults to 'kiips.app'. */
  domain?: string | undefined;
  /** Optional public URL for the trip; emitted as VEVENT URL on each event. */
  tripUrl?: string | undefined;
  /** Optional footer appended to the DESCRIPTION of booking-relevant
   *  events (Journey, Stay). Used to surface affiliate booking links
   *  in calendar exports — the URL lives for the trip duration in the
   *  subscriber's calendar, which is high-leverage multi-channel
   *  exposure. Caller composes the full text; the builder only
   *  appends it (separated by a blank line) when present. Skipped on
   *  Place / CustomEntry events so the per-stop view stays clean. */
  bookingFooter?: string | undefined;
}

export function buildIcs(trip: Trip, options: BuildIcsOptions = {}): string {
  const domain = options.domain ?? 'kiips.app';
  // RFC 5545 §3.6.1 requires DTSTAMP. Prefer trip.updatedAt; if it's
  // missing or unparseable, derive a UTC stamp directly from `Date`.
  const dtstamp =
    formatFloating(trip.updatedAt) ?? nowAsFloatingUtc();

  const lines: string[] = [];
  lines.push('BEGIN:VCALENDAR');
  lines.push('VERSION:2.0');
  lines.push(`PRODID:${PRODID}`);
  lines.push('CALSCALE:GREGORIAN');
  lines.push('METHOD:PUBLISH');
  lines.push(`X-WR-CALNAME:${escapeText(trip.name)}`);
  // Prefer the picked `destinationPlace` label (ADR-0054) — it carries
  // the catalog's canonical name and an ISO-2 country code we can
  // surface to calendar consumers. Falls back to the free-text
  // `destination` for legacy trips that pre-date the picker.
  const destinationLabel = trip.destinationPlace?.name
    ? trip.destinationPlace.countryCode
      ? `${trip.destinationPlace.name} (${trip.destinationPlace.countryCode})`
      : trip.destinationPlace.name
    : trip.destination;
  if (destinationLabel) {
    lines.push(`X-WR-CALDESC:${escapeText(`Trip to ${destinationLabel}`)}`);
  }

  for (const day of trip.days ?? []) {
    for (const place of day.places ?? []) {
      const ev = placeEvent(place, day, trip, domain, dtstamp, options.tripUrl);
      if (ev) lines.push(...ev);
    }
    for (const custom of day.customs ?? []) {
      const ev = customEvent(custom, day, trip, domain, dtstamp, options.tripUrl);
      if (ev) lines.push(...ev);
    }
  }

  for (const journey of trip.journeys ?? []) {
    const ev = journeyEvent(
      journey,
      trip,
      domain,
      dtstamp,
      options.tripUrl,
      options.bookingFooter,
    );
    if (ev) lines.push(...ev);
  }

  for (const stay of trip.stays ?? []) {
    const ev = stayEvent(
      stay,
      trip,
      domain,
      dtstamp,
      options.tripUrl,
      options.bookingFooter,
    );
    if (ev) lines.push(...ev);
  }

  lines.push('END:VCALENDAR');

  // RFC 5545 line folding (75 octets) + CRLF terminators. Concatenate
  // first so multi-line escaped fields fold consistently.
  return lines.flatMap(foldLine).join('\r\n') + '\r\n';
}

/**
 * Build the LOCATION field for a venue-bearing event (place / stay).
 * Calendar apps text-geocode this when GEO is missing; combining the
 * name + address gives Apple Calendar / Google Calendar enough to find
 * a map pin even when the catalog didn't carry coords. Falls back to
 * whichever half is present so we always emit something useful.
 */
function combineLocation(name: string | undefined, address: string | undefined): string | undefined {
  const n = name?.trim();
  const a = address?.trim();
  if (n && a) return `${n}, ${a}`;
  return n || a || undefined;
}

function placeEvent(
  place: Place,
  day: Day,
  trip: Trip,
  domain: string,
  dtstamp: string,
  tripUrl: string | undefined,
): string[] | null {
  if (!day.date) return null;
  const start = combineDayAndTime(day.date, place.startTime);
  const end = combineDayAndTime(day.date, place.endTime ?? place.startTime);
  if (!start) return null;
  return event({
    uid: `place-${place.id}@${domain}`,
    dtstamp,
    summary: place.customLabel || place.name || 'Untitled place',
    description: place.notes,
    location: combineLocation(place.name, place.address),
    geo: place.lat != null && place.lng != null ? { lat: place.lat, lng: place.lng } : undefined,
    start,
    end: end && end > start ? end : addMinutes(start, 60),
    url: tripUrl,
    tripName: trip.name,
  });
}

function customEvent(
  custom: CustomEntry,
  day: Day,
  trip: Trip,
  domain: string,
  dtstamp: string,
  tripUrl: string | undefined,
): string[] | null {
  if (!day.date) return null;
  const start = combineDayAndTime(day.date, custom.startTime);
  const end = combineDayAndTime(day.date, custom.endTime ?? custom.startTime);
  if (!start) return null;
  const summary = [custom.title, custom.subtitle].filter(Boolean).join(' — ');
  return event({
    uid: `custom-${custom.id}@${domain}`,
    dtstamp,
    summary: summary || 'Untitled entry',
    description: custom.notes,
    start,
    end: end && end > start ? end : addMinutes(start, 60),
    url: tripUrl,
    tripName: trip.name,
  });
}

function journeyEvent(
  journey: Journey,
  trip: Trip,
  domain: string,
  dtstamp: string,
  tripUrl: string | undefined,
  bookingFooter: string | undefined,
): string[] | null {
  const start = formatFloating(journey.origin?.time);
  const end = formatFloating(journey.destination?.time);
  if (!start || !end) return null;
  const summary = journeySummary(journey);
  const description = appendBookingFooter(
    [
      journey.carrier && journey.serviceNumber
        ? `${journey.carrier} ${journey.serviceNumber}`
        : journey.carrier ?? journey.serviceNumber,
      journey.travelClass,
      journey.seat ? `Seat ${journey.seat}` : undefined,
      journey.booking?.confirmationCode ? `Conf ${journey.booking.confirmationCode}` : undefined,
      journey.booking?.ref ? `Ref ${journey.booking.ref}` : undefined,
      journey.notes,
    ]
      .filter(Boolean)
      .join('\n'),
    bookingFooter,
  );
  const location = anchorLocation(journey.origin) || undefined;
  return event({
    uid: `journey-${journey.id}@${domain}`,
    dtstamp,
    summary,
    description: description || undefined,
    location,
    start,
    end: end > start ? end : addMinutes(start, 60),
    url: journey.booking?.bookingUrl || tripUrl,
    tripName: trip.name,
  });
}

function stayEvent(
  stay: Stay,
  trip: Trip,
  domain: string,
  dtstamp: string,
  tripUrl: string | undefined,
  bookingFooter: string | undefined,
): string[] | null {
  const start = formatFloating(stay.checkIn);
  const end = formatFloating(stay.checkOut);
  if (!start || !end) return null;
  const description = appendBookingFooter(
    [
      stay.roomType,
      stay.unitNumber ? `Unit ${stay.unitNumber}` : undefined,
      stay.booking?.confirmationCode ? `Conf ${stay.booking.confirmationCode}` : undefined,
      stay.booking?.ref ? `Ref ${stay.booking.ref}` : undefined,
      stay.notes,
    ]
      .filter(Boolean)
      .join('\n'),
    bookingFooter,
  );
  return event({
    uid: `stay-${stay.id}@${domain}`,
    dtstamp,
    summary: `Stay at ${stay.customLabel || stay.place.name}`,
    description: description || undefined,
    // ADR-0055: `place` is the canonical source post-cutover; on-read
    // migration in the mobile store guarantees `place` exists, but
    // iCal can be built server-side from any Trip wire payload, so
    // we still null-check the optional snapshot fields here.
    location: combineLocation(stay.place.name, stay.place.address),
    geo: stay.place.lat != null && stay.place.lng != null
      ? { lat: stay.place.lat, lng: stay.place.lng }
      : undefined,
    start,
    end: end > start ? end : addMinutes(start, 60),
    url: stay.booking?.bookingUrl || tripUrl,
    tripName: trip.name,
  });
}

function journeySummary(journey: Journey): string {
  const carrier = journey.serviceNumber
    ? [journey.carrier, journey.serviceNumber].filter(Boolean).join(' ')
    : journey.carrier;
  // ADR-0055: prefer code (IATA / station), then customLabel, then
  // the catalog place name. The route line wants the shortest stable
  // identifier — codes are tighter for transit cards than the full
  // catalog name.
  const route = [
    journey.origin?.code ?? journey.origin?.customLabel ?? journey.origin?.place.name,
    journey.destination?.code ?? journey.destination?.customLabel ?? journey.destination?.place.name,
  ].filter(Boolean).join(' → ');
  if (carrier && route) return `${carrier}: ${route}`;
  if (carrier) return carrier;
  if (route) return route;
  return modeLabel(journey.mode);
}

function modeLabel(mode: Journey['mode']): string {
  switch (mode) {
    case 'flight': return 'Flight';
    case 'train': return 'Train';
    case 'bus': return 'Bus';
    case 'ferry': return 'Ferry';
    case 'car-rental': return 'Car';
    case 'transfer': return 'Transfer';
  }
}

function anchorLocation(anchor: Journey['origin'] | undefined): string {
  if (!anchor) return '';
  // ADR-0055: customLabel is for display titles (calendar SUMMARY);
  // LOCATION should remain the catalog place + address so calendar
  // apps can geocode reliably.
  const parts = [anchor.place.name];
  if (anchor.place.address) parts.push(anchor.place.address);
  if (anchor.terminal) parts.push(`Terminal ${anchor.terminal}`);
  if (anchor.gate) parts.push(`Gate ${anchor.gate}`);
  if (anchor.platform) parts.push(`Platform ${anchor.platform}`);
  return parts.filter(Boolean).join(', ');
}

/** Join a base description with an optional footer separated by a
 *  blank line. Keeps the original description (confirmation refs,
 *  notes) visually distinct from the appended booking-link line in
 *  calendar clients. Returns the footer alone when the base is
 *  empty, and the base alone when the footer is empty. */
function appendBookingFooter(
  base: string,
  footer: string | undefined,
): string {
  const trimmed = footer?.trim();
  if (!trimmed) return base;
  if (!base) return trimmed;
  return `${base}\n\n${trimmed}`;
}

interface EventInput {
  uid: string;
  dtstamp: string;
  summary: string;
  description?: string | undefined;
  location?: string | undefined;
  geo?: { lat: number; lng: number } | undefined;
  start: string;
  end: string;
  url?: string | undefined;
  tripName: string;
}

function event(input: EventInput): string[] {
  const out: string[] = [];
  out.push('BEGIN:VEVENT');
  out.push(`UID:${input.uid}`);
  out.push(`DTSTAMP:${input.dtstamp}`);
  out.push(`DTSTART:${input.start}`);
  out.push(`DTEND:${input.end}`);
  out.push(`SUMMARY:${escapeText(input.summary)}`);
  if (input.description) out.push(`DESCRIPTION:${escapeText(input.description)}`);
  if (input.location) out.push(`LOCATION:${escapeText(input.location)}`);
  if (input.geo) out.push(`GEO:${input.geo.lat.toFixed(6)};${input.geo.lng.toFixed(6)}`);
  if (input.url) out.push(`URL:${input.url}`);
  out.push(`CATEGORIES:${escapeText(input.tripName)}`);
  out.push('END:VEVENT');
  return out;
}

/**
 * Combine 'YYYY-MM-DD' + 'HH:mm' into floating-time 'YYYYMMDDTHHMMSS'.
 * Returns undefined for missing date/time so events without a start get
 * filtered out rather than silently wrong.
 */
function combineDayAndTime(date: string, time: string | undefined): string | undefined {
  if (!date || !time) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m || !t) return undefined;
  const hh = String(Math.min(23, Math.max(0, Number.parseInt(t[1]!, 10)))).padStart(2, '0');
  const mm = String(Math.min(59, Math.max(0, Number.parseInt(t[2]!, 10)))).padStart(2, '0');
  return `${m[1]}${m[2]}${m[3]}T${hh}${mm}00`;
}

/**
 * Normalise an ISO-ish datetime ('2026-10-21T23:00' or full ISO with Z)
 * to floating local 'YYYYMMDDTHHMMSS'. UTC inputs (trailing Z) are
 * converted to UTC clock time + 'Z' so they survive timezone changes.
 */
function formatFloating(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const utc = /Z$/.test(iso) || /[+-]\d{2}:?\d{2}$/.test(iso);
  if (utc) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return undefined;
    return (
      d.getUTCFullYear().toString().padStart(4, '0') +
      String(d.getUTCMonth() + 1).padStart(2, '0') +
      String(d.getUTCDate()).padStart(2, '0') + 'T' +
      String(d.getUTCHours()).padStart(2, '0') +
      String(d.getUTCMinutes()).padStart(2, '0') +
      String(d.getUTCSeconds()).padStart(2, '0') + 'Z'
    );
  }
  // Floating local datetime: '2026-10-21T23:00' or '2026-10-21T23:00:00'.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(iso);
  if (!m) return undefined;
  return `${m[1]}${m[2]}${m[3]}T${m[4]}${m[5]}${m[6] ?? '00'}`;
}

function nowAsFloatingUtc(): string {
  const d = new Date();
  return (
    d.getUTCFullYear().toString().padStart(4, '0') +
    String(d.getUTCMonth() + 1).padStart(2, '0') +
    String(d.getUTCDate()).padStart(2, '0') + 'T' +
    String(d.getUTCHours()).padStart(2, '0') +
    String(d.getUTCMinutes()).padStart(2, '0') +
    String(d.getUTCSeconds()).padStart(2, '0') + 'Z'
  );
}

function addMinutes(floating: string, minutes: number): string {
  // Parse 'YYYYMMDDTHHMMSS' or trailing 'Z' form.
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/.exec(floating);
  if (!m) return floating;
  const d = new Date(Date.UTC(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4]), Number(m[5]), Number(m[6]),
  ));
  d.setUTCMinutes(d.getUTCMinutes() + minutes);
  return (
    d.getUTCFullYear().toString().padStart(4, '0') +
    String(d.getUTCMonth() + 1).padStart(2, '0') +
    String(d.getUTCDate()).padStart(2, '0') + 'T' +
    String(d.getUTCHours()).padStart(2, '0') +
    String(d.getUTCMinutes()).padStart(2, '0') +
    String(d.getUTCSeconds()).padStart(2, '0') + m[7]
  );
}

/** RFC 5545 §3.3.11: escape backslashes, commas, semicolons, newlines. */
function escapeText(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\r|\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

/**
 * RFC 5545 §3.1: lines must not exceed 75 octets. Continuation lines start
 * with a single space. We measure octets via TextEncoder so we don't split
 * a multi-byte UTF-8 sequence in half.
 */
function foldLine(line: string): string[] {
  const enc = new TextEncoder();
  const bytes = enc.encode(line);
  if (bytes.length <= 75) return [line];

  const out: string[] = [];
  const dec = new TextDecoder();
  let cursor = 0;
  let isContinuation = false;
  while (cursor < bytes.length) {
    const remaining = 75 - (isContinuation ? 1 : 0);
    let take = Math.min(remaining, bytes.length - cursor);
    // Don't split a UTF-8 continuation byte (10xxxxxx).
    while (take > 0 && (bytes[cursor + take]! & 0xc0) === 0x80) take -= 1;
    if (take <= 0) take = remaining; // pathological; emit as-is
    const chunk = dec.decode(bytes.slice(cursor, cursor + take));
    out.push(isContinuation ? ' ' + chunk : chunk);
    cursor += take;
    isContinuation = true;
  }
  return out;
}
